import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { resolveImageUrl, formatPostDate } from '../lib/blogApi';
import { Calendar, User, Users, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { getCartItemCount } = useCart();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState('All');

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/blog/posts`)
      .then((res) => {
        if (active) setPosts(res.data);
      })
      .catch(() => {
        if (active) setPosts([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const categories = useMemo(() => {
    const tags = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => tags.add(t)));
    return ['All', ...Array.from(tags)];
  }, [posts]);

  const visiblePosts = activeTag === 'All' ? posts : posts.filter((p) => (p.tags || []).includes(activeTag));

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              The <span className="text-[#F39C49]">Workshop</span>
            </h1>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto">
              Stories, guides, and insights for women in automotive refinishing. Learn from the community, for the community.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      {categories.length > 1 && (
        <section className="py-8 border-b-4 border-[#004D51]">
          <div className="container mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveTag(category)}
                  data-testid={`blog-filter-${category}`}
                  className={`px-4 py-2 rounded-full font-bold transition-colors ${
                    activeTag === category ? 'bg-[#DB5A14] text-white' : 'text-[#545759] bg-[#F3CEB2]/30 hover:bg-[#F3CEB2]/60'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 text-[#004D51] animate-spin" /></div>
          ) : visiblePosts.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center py-12">
              <div className="w-24 h-24 bg-[#F3CEB2]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                <Users className="w-12 h-12 text-[#004D51]" />
              </div>
              <h2 className="text-3xl font-black text-[#004D51] mb-4">Watch This Space</h2>
              <p className="text-xl text-[#545759]">
                We&apos;re just getting started. Check back soon for stories and guides from the Vesta community.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {visiblePosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group bg-white border-4 border-[#004D51] rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col"
                >
                  <div className="relative aspect-video overflow-hidden bg-gray-100">
                    {post.cover_image ? (
                      <img
                        src={resolveImageUrl(post.cover_image)}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#004D51] to-[#003338]" />
                    )}
                    {post.tags?.[0] && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#DB5A14] text-white text-xs font-bold px-3 py-1 rounded-full">{post.tags[0]}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-sm text-[#545759] mb-3">
                      <span className="flex items-center"><User className="w-4 h-4 mr-1" />{post.author}</span>
                      <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatPostDate(post.published_at || post.created_at)}</span>
                    </div>
                    <h2 className="text-2xl font-black text-[#004D51] mb-3 group-hover:text-[#DB5A14] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-[#545759] line-clamp-3 flex-1">{post.excerpt}</p>
                    <span className="inline-flex items-center text-[#DB5A14] font-bold mt-4 group-hover:translate-x-1 transition-transform">
                      Read more <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-[#004D51] text-white">
        <div className="container mx-auto px-6">
          <h3 className="text-2xl font-black text-center mb-8">While You&apos;re Here...</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/fit">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004D51] font-bold">
                View Fit &amp; Care Guide
              </Button>
            </Link>
            <Link to="/about-vesta">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004D51] font-bold">
                Learn About Vesta
              </Button>
            </Link>
            <Link to="/shop">
              <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold">Shop Now</Button>
            </Link>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default Blog;
